import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminAlerts } from './admin-alerts';
import { resolveClock } from '../../../function-utils/building-time';
import { ClockSurfaceProvider } from '../../../context/clock-context';
import { ClockNote } from '../../clock-note/clock-note';
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  useSearchParams,
  Navigate,
} from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { useAuth } from '../../../context/auth-provider';
import { palette, spacing, typography } from '../../../style-utils/theme';
import * as api from '../../../api/admin-client';
import { IntegrationScreen } from './admin-integrations';
import { GROUPS, sectionFor, visibleSections } from './admin-sections';
// IS8.0 (IS8-D4): the Lobbero Stays sections exist only while Stays is on.
import { useStaysProduct } from '../../../utils/app-features';
import { useElevation } from './use-admin';
import { ElevateDialog, ConfirmDialog, ConfirmSpec } from './elevate-dialog';
import {
  OverviewScreen,
  ListScreen,
  PerformanceScreen,
  DatabaseScreen,
  HealthScreen,
  UsageScreen,
  IqScreen,
  NotificationsScreen,
  IntegrityScreen,
  StorageScreen,
  CredentialsScreen,
  CataloguesScreen,
  StaysReservationsScreen,
  StaysSignupsScreen,
  StaysBillingScreen,
  StaysAccountsScreen,
  StaysClosuresScreen,
  ORG_COLUMNS,
  USER_COLUMNS,
  WAITLIST_COLUMNS,
  BUILDING_COLUMNS,
  UNIT_COLUMNS,
  RESIDENT_COLUMNS,
  SUBSCRIPTION_COLUMNS,
  INVITATION_COLUMNS,
  LOG_COLUMNS,
  PACKAGE_COLUMNS,
  VISITOR_PASS_COLUMNS,
  SHIFT_COLUMNS,
  ACCESS_COLUMNS,
  SESSION_COLUMNS,
  AI_COLUMNS,
  AUDIT_COLUMNS,
  STRIPE_COLUMNS,
  PORTFOLIO_COLUMNS,
} from './admin-screens';
import { labelFor } from './admin-table';
import { PromosScreen, PromoDetail, RedemptionsScreen } from './admin-promos';
import { BackupsScreen, ServerScreen, SystemLogsScreen } from './admin-ops';
import {
  OrganizationDetail,
  UserDetail,
  BuildingDetail,
  PortfolioDetail,
  UnitDetail,
  AdminsScreen,
} from './admin-details';
import {
  AdminRoot,
  Rail,
  RailBrand,
  RailTag,
  RailGroup,
  RailGroupLabel,
  RailLink,
  Main,
  TopBar,
  Crumbs,
  CrumbLink,
  SearchBox,
  Content,
  PageTitle,
  PageSub,
  Card,
  CardBody,
  ElevationChip,
  Btn,
  SignOutBtn,
  Empty,
  Kbd,
  NoteBox,
  ErrorBox,
  Spacer,
  DesktopOnly,
  Mono,
} from './admin.styled';

/**
 * THE SUPER ADMIN CONSOLE.
 *
 * ── THE GUARD HERE IS A COURTESY, NOT A CONTROL ────────────────────────────
 *
 * `user.is_platform_admin` decides whether this component renders at all, and
 * an ordinary customer who types /admin is redirected. That is UX. The actual
 * control is on the server: every one of the console's 47 endpoints asks
 * `is_platform_admin()` against the database on the request itself and answers
 * 404 to anyone else, so a forged flag reaches an empty console and nothing
 * more. Never move a check from there to here.
 *
 * ── ONE OWNER OF THE STEP-UP STATE ─────────────────────────────────────────
 *
 * Sensitive actions live on the detail screens, but none of them owns the
 * password prompt. A screen hands a `ConfirmSpec` up here; the shell decides
 * whether an elevation is needed, prompts for it, and only then shows the
 * confirmation. Written per screen, that flow would be half-implemented
 * somewhere within a month — and the half that gets missed is always the
 * re-prompt after the ten minutes lapse.
 *
 * ── KEYBOARD FIRST ─────────────────────────────────────────────────────────
 *
 * `/` focuses search from anywhere, `g` then a letter jumps to a section, Escape
 * closes a dialog. An operations console that needs a mouse to change screens is
 * a slow one, and this is a tool people use for hours.
 */

const AdminSearch: React.FC = () => {
  // HYDRATED FROM `?q=`, NOT FROM EMPTY. The header's quick search navigates
  // here with the query in the URL (see the Enter handler in AdminShell), so
  // starting at '' threw the submitted query away: the box came up blank and
  // the page said "Type to search" over a URL that already carried the term.
  // The debounce effect below keys on `term`, so seeding it is the whole fix —
  // the search fires on its own once the value is there.
  const [params, setParams] = useSearchParams();
  const urlTerm = params.get('q') ?? '';
  const [term, setTerm] = useState(urlTerm);
  const [res, setRes] = useState<{
    results: api.SearchResult[];
    limits: string[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    box.current?.focus();
  }, []);

  // Arriving from the header a SECOND time only changes the URL — this
  // component is already mounted, so the initial state above does not re-run.
  useEffect(() => {
    setTerm(prev => (prev === urlTerm ? prev : urlTerm));
  }, [urlTerm]);

  useEffect(() => {
    if (!term.trim()) {
      setRes(null);

      return undefined;
    }
    const t = setTimeout(() => {
      setBusy(true);
      api
        .search(term.trim())
        .then(setRes)
        .catch(() => setRes(null))
        .finally(() => setBusy(false));
    }, 250);

    return () => clearTimeout(t);
  }, [term]);

  return (
    <>
      {/* A DISTINCT ACCESSIBLE NAME from the top bar's box, and that is not
          cosmetic: two controls sharing "Search the platform" gives a screen
          reader two identically named landmarks on the same page and no way to
          tell which one searches inline. Caught by the Playwright walk, which
          could not address either of them unambiguously. */}
      <SearchBox
        ref={box}
        value={term}
        onChange={e => {
          const next = e.target.value;
          setTerm(next);
          // `replace`, not push: typing must not put one history entry per
          // keystroke between the operator and the page they came from.
          setParams(next ? { q: next } : {}, { replace: true });
        }}
        placeholder="Organization, building, unit, an exact email, or any id"
        aria-label="Search organizations, buildings, units and people"
        style={{ maxWidth: '100%' }}
      />
      {res?.limits?.length ? (
        <NoteBox>
          {res.limits.map(l => (
            <div key={l}>{l}</div>
          ))}
        </NoteBox>
      ) : null}
      <Card>
        <CardBody>
          {busy ? (
            <Empty>Searching…</Empty>
          ) : !res ? (
            <Empty>Type to search across every organization.</Empty>
          ) : !res.results.length ? (
            <Empty>Nothing matched.</Empty>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {res.results.map(r => (
                <li key={`${r.type}-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => nav(r.href)}
                    style={{
                      display: 'flex',
                      gap: spacing.xs,
                      alignItems: 'baseline',
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      borderBottom: `1px solid ${palette.line}`,
                      background: 'none',
                      padding: `${spacing.xs} 0`,
                      font: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        fontSize: typography.size.xxs,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        color: palette.slate,
                        minWidth: 96,
                      }}
                    >
                      {r.type}
                    </span>
                    <strong>{r.label}</strong>
                    <span style={{ color: palette.slate }}>{r.sub}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export const AdminConsole: React.FC = () => {
  // Resolved per render, not once: a laptop changes zone when it travels.
  const consoleClock = resolveClock({ surface: 'console' });
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const elevation = useElevation();

  const [pending, setPending] = useState<ConfirmSpec | null>(null);
  const [askPassword, setAskPassword] = useState(false);
  const topSearch = useRef<HTMLInputElement>(null);
  const [quick, setQuick] = useState('');

  const isAdmin = Boolean(
    (user as unknown as Record<string, unknown>)?.is_platform_admin
  );

  const staysOn = useStaysProduct() === true;
  const SECTIONS = useMemo(() => visibleSections(staysOn), [staysOn]);
  const segment = pathname.replace(/^\/admin\/?/, '').split('/')[0] || '';
  const section = sectionFor(segment, SECTIONS);
  const detailId = pathname.replace(/^\/admin\/?/, '').split('/')[1] || null;

  useDocumentTitle(
    section ? `${section.label} · Lobbero operations` : 'Lobbero operations'
  );

  /* ── the action pipeline ─────────────────────────────────────────────── */
  const requestAction = useCallback(
    (spec: ConfirmSpec) => {
      if (elevation.elevated) setPending(spec);
      else {
        // Hold the intent, prove who you are, THEN confirm. Losing the intent
        // here would make every sensitive action a two-attempt affair.
        setPending(spec);
        setAskPassword(true);
      }
    },
    [elevation.elevated]
  );

  /* ── keyboard ────────────────────────────────────────────────────────── */
  useEffect(() => {
    let awaitingJump = false;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (typing) return;

      if (e.key === '/') {
        e.preventDefault();
        topSearch.current?.focus();

        return;
      }
      if (e.key === 'g') {
        awaitingJump = true;
        setTimeout(() => {
          awaitingJump = false;
        }, 1200);

        return;
      }
      if (awaitingJump) {
        const target = SECTIONS.find(s => s.key === e.key.toLowerCase());
        awaitingJump = false;
        if (target) {
          e.preventDefault();
          nav(`/admin/${target.path}`);
        }
      }
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [nav, SECTIONS]);

  const grouped = useMemo(
    () => GROUPS.map(g => ({ group: g, items: SECTIONS.filter(s => s.group === g) })),
    [SECTIONS]
  );

  if (!isAdmin) {
    // A customer who guesses the URL. Not an error page — there is nothing here
    // for them, and saying so at length would confirm the console exists.
    return <Navigate to="/" replace />;
  }

  const host = { requestAction, elevated: elevation.elevated };

  return (
    /* THE CONSOLE IS THE READER'S CLOCK (R-T6), whatever role the operator's
       account holds — the surface says so, so nothing inside it can fall back
       to "role unknown, so the building's". */
    <ClockSurfaceProvider surface="console">
      <AdminRoot>
        <Rail aria-label="Operations sections">
          <RailBrand>
            Lobbero <RailTag>ops</RailTag>
          </RailBrand>
          {grouped.map(({ group, items }) => (
            <RailGroup key={group}>
              <RailGroupLabel>{group}</RailGroupLabel>
              {items.map(s => (
                <RailLink
                  key={s.path}
                  type="button"
                  active={segment === s.path}
                  aria-current={segment === s.path ? 'page' : undefined}
                  onClick={() => nav(`/admin/${s.path}`)}
                >
                  {s.label}
                </RailLink>
              ))}
            </RailGroup>
          ))}
        </Rail>

        <Main>
          <TopBar>
            <Crumbs aria-label="Breadcrumb">
              <CrumbLink onClick={() => nav('/admin')}>Operations</CrumbLink>
              {section && section.path ? (
                <>
                  <span aria-hidden>/</span>
                  <CrumbLink onClick={() => nav(`/admin/${section.path}`)}>
                    {section.label}
                  </CrumbLink>
                </>
              ) : null}
              {detailId ? (
                <>
                  <span aria-hidden>/</span>
                  <Mono>
                    {detailId.length > 18 ? `${detailId.slice(0, 14)}…` : detailId}
                  </Mono>
                </>
              ) : null}
            </Crumbs>

            <Spacer />

            <SearchBox
              ref={topSearch}
              value={quick}
              placeholder="Search  ( / )"
              aria-label="Quick search — press Enter for full results"
              onChange={e => setQuick(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && quick.trim()) {
                  nav(`/admin/search?q=${encodeURIComponent(quick.trim())}`);
                }
                if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
              }}
            />

            {/* NAME THE NOUN. This chip is the step-up elevation state, but a
              bare "not confirmed" in the global header was read as the
              account's email status — QA filed it as contradicting a user
              whose email was verified. The word it was missing is "identity". */}
            <ElevationChip live={elevation.elevated}>
              {elevation.elevated
                ? `Identity confirmed · ${Math.ceil(elevation.remaining / 60)}m`
                : 'Identity not confirmed'}
            </ElevationChip>
            {elevation.elevated ? (
              <DesktopOnly>
                <Btn onClick={elevation.stepDown}>Step down</Btn>
              </DesktopOnly>
            ) : null}
            {/* The shared logout: revokes this device's session server-side, clears
              the httpOnly cookies and routes to sign-in (auth-provider.tsx). */}
            <SignOutBtn type="button" onClick={() => logout()}>
              Sign out
            </SignOutBtn>
          </TopBar>

          <Content>
            {/* Above every page's title, on every page: an operator must not have
              to open the right screen to learn the product has stopped
              sending email (see admin-alerts.tsx). */}
            <AdminAlerts />
            {section ? (
              <div>
                <PageTitle>{section.label}</PageTitle>
                {section.blurb ? <PageSub>{section.blurb}</PageSub> : null}
                {/* Said once, on every console page: these are YOUR times, not a
                  building's and not UTC (R-T6, R-T9). */}
                <ClockNote clock={consoleClock} />
              </div>
            ) : null}

            <Routes>
              <Route index element={<OverviewScreen />} />
              <Route path="search" element={<AdminSearch />} />

              <Route
                path="organizations"
                element={
                  <ListScreen
                    resource="organizations"
                    columns={ORG_COLUMNS}
                    searchable
                    searchPlaceholder="Name, id, owner email, Stripe id…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Account',
                        options: [
                          { value: 'active', label: 'Active' },
                          { value: 'suspended', label: 'Suspended' },
                          { value: 'closed', label: 'Closed' },
                        ],
                      },
                      {
                        name: 'sub_status',
                        label: 'Subscription',
                        options: [
                          'trialing',
                          'active',
                          'past_due',
                          'canceled',
                          'unpaid',
                          'paused',
                          'incomplete',
                          'incomplete_expired',
                        ].map(v => ({ value: v, label: v })),
                      },
                    ]}
                    onRowClick={(r, n2) =>
                      n2(`/admin/organizations/${encodeURIComponent(r.id)}`)
                    }
                  />
                }
              />
              <Route
                path="organizations/:id"
                element={<OrganizationDetail {...host} />}
              />

              <Route
                path="waitlist"
                element={
                  <ListScreen
                    resource="waitlist"
                    columns={WAITLIST_COLUMNS}
                    searchable
                    searchPlaceholder="Any fragment of an address, role or source…"
                    filters={[
                      {
                        name: 'product',
                        label: 'Product',
                        options: [{ value: 'stays', label: 'Lobbero Stays' }],
                      },
                      {
                        name: 'status',
                        label: 'Status',
                        options: ['new', 'invited', 'declined', 'unsubscribed'].map(
                          v => ({
                            value: v,
                            label: v,
                          })
                        ),
                      },
                    ]}
                    note={
                      <>
                        Addresses are stored as ciphertext and decrypted for this screen
                        only. A whole address in the box resolves through the blind index;
                        a fragment scans and decrypts, so it is slower on a long list.
                      </>
                    }
                    emptyLabel="Nobody is on a waitlist yet."
                  />
                }
              />

              <Route
                path="users"
                element={
                  <ListScreen
                    resource="users"
                    columns={USER_COLUMNS}
                    searchable
                    searchPlaceholder="Name, email, phone, organization…"
                    filters={[
                      {
                        name: 'verified',
                        label: 'Email',
                        options: [
                          { value: 'true', label: 'Verified' },
                          { value: 'false', label: 'Unverified' },
                        ],
                      },
                      {
                        name: 'role',
                        label: 'Role',
                        options: [
                          { value: 'owner', label: 'Owner' },
                          { value: 'property_manager', label: 'Property manager' },
                          { value: 'concierge', label: 'Concierge' },
                          { value: 'none', label: 'No staff role' },
                        ],
                      },
                      {
                        name: 'status',
                        label: 'Status',
                        options: [
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                        ],
                      },
                    ]}
                    onRowClick={(r, n2) => n2(`/admin/users/${encodeURIComponent(r.id)}`)}
                  />
                }
              />
              <Route path="users/:id" element={<UserDetail {...host} />} />

              <Route path="admins" element={<AdminsScreen {...host} />} />

              <Route
                path="portfolios"
                element={
                  <ListScreen
                    resource="portfolios"
                    columns={PORTFOLIO_COLUMNS}
                    searchable
                    searchPlaceholder="Portfolio, code, organization, owner email…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: [
                          { value: 'active', label: 'Active' },
                          { value: 'archived', label: 'Archived' },
                        ],
                      },
                      {
                        name: 'is_default',
                        label: 'Default',
                        options: [
                          { value: 'true', label: 'Default only' },
                          { value: 'false', label: 'Non-default' },
                        ],
                      },
                    ]}
                    onRowClick={(r, n2) =>
                      n2(`/admin/portfolios/${encodeURIComponent(r.id)}`)
                    }
                  />
                }
              />
              <Route path="portfolios/:id" element={<PortfolioDetail />} />
              <Route
                path="buildings"
                element={
                  <ListScreen
                    resource="buildings"
                    columns={BUILDING_COLUMNS}
                    searchable
                    searchPlaceholder="Name, address, city, organization, owner email…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: [
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                        ],
                      },
                    ]}
                    onRowClick={(r, n2) => n2(`/admin/buildings/${r.id}`)}
                  />
                }
              />
              <Route path="buildings/:id" element={<BuildingDetail />} />

              <Route
                path="units"
                element={
                  <ListScreen
                    resource="units"
                    columns={UNIT_COLUMNS}
                    searchable
                    searchPlaceholder="Unit, building, city, organization…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: [
                          { value: 'active', label: 'Active' },
                          { value: 'offline', label: 'Offline' },
                          { value: 'archived', label: 'Returned' },
                        ],
                      },
                      {
                        name: 'kind',
                        label: 'Kind',
                        options: [
                          { value: 'residential', label: 'Residential' },
                          { value: 'commercial', label: 'Commercial' },
                          { value: 'parking', label: 'Parking' },
                          { value: 'storage', label: 'Storage' },
                        ],
                      },
                    ]}
                    onRowClick={(r, n2) => n2(`/admin/units/${encodeURIComponent(r.id)}`)}
                  />
                }
              />
              <Route path="units/:id" element={<UnitDetail />} />

              <Route
                path="residents"
                element={
                  <ListScreen
                    resource="residents"
                    columns={RESIDENT_COLUMNS}
                    searchable
                    searchPlaceholder="Resident name, unit, building, organization…"
                    note={
                      <NoteBox>
                        Tenancy facts only — who lives where, and since when. Visitor
                        history, parcels and log entries about a resident stay inside the
                        customer&apos;s own product.
                      </NoteBox>
                    }
                    filters={[
                      {
                        name: 'open',
                        label: 'Tenancy',
                        options: [
                          { value: 'true', label: 'Current' },
                          { value: 'false', label: 'Ended' },
                        ],
                      },
                    ]}
                  />
                }
              />

              <Route
                path="subscriptions"
                element={
                  <ListScreen
                    resource="subscriptions"
                    columns={SUBSCRIPTION_COLUMNS}
                    searchable
                    searchPlaceholder="Organization, owner email, plan, cus_… or sub_…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: [
                          'trialing',
                          'active',
                          'past_due',
                          'canceled',
                          'unpaid',
                          'paused',
                          'incomplete',
                          'incomplete_expired',
                        ].map(v => ({ value: v, label: v })),
                      },
                      {
                        name: 'cancelling',
                        label: 'Cancelling',
                        options: [
                          { value: 'true', label: 'At period end' },
                          { value: 'false', label: 'Not cancelling' },
                        ],
                      },
                    ]}
                    onRowClick={(r, n2) =>
                      n2(`/admin/organizations/${encodeURIComponent(r.organization_id)}`)
                    }
                  />
                }
              />
              <Route path="usage" element={<UsageScreen />} />
              <Route path="catalogues" element={<CataloguesScreen />} />
              {/* Promotions. `host` carries requestAction, which is what makes
                every mutation ask for the password and a reason before it runs —
                the same shell every other sensitive act in this console goes
                through, rather than a second confirmation flow written here. */}
              <Route path="promos" element={<PromosScreen {...host} />} />
              <Route path="promos/:id" element={<PromoDetail {...host} />} />
              <Route path="redemptions" element={<RedemptionsScreen />} />
              <Route
                path="stripe"
                element={
                  <ListScreen
                    resource="stripe/events"
                    columns={STRIPE_COLUMNS}
                    searchable
                    searchPlaceholder="Event type, or a cus_… / sub_… / in_… object id"
                    filters={[
                      {
                        name: 'processed',
                        label: 'Processed',
                        options: [
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' },
                        ],
                      },
                    ]}
                    note={
                      <NoteBox>
                        Event payloads are deliberately not returned — they carry billing
                        addresses and card metadata.
                      </NoteBox>
                    }
                  />
                }
              />

              <Route
                path="invitations"
                element={
                  <ListScreen
                    resource="invitations"
                    columns={INVITATION_COLUMNS}
                    searchable
                    searchPlaceholder="Email, role, organization, building…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: ['pending', 'accepted', 'revoked', 'expired'].map(v => ({
                          value: v,
                          label: v,
                        })),
                      },
                    ]}
                  />
                }
              />
              <Route
                path="logs"
                element={
                  <ListScreen
                    resource="logs"
                    columns={LOG_COLUMNS}
                    searchable
                    searchPlaceholder="Entry type, author, building, organization…"
                    filters={[
                      {
                        name: 'kind',
                        label: 'Kind',
                        options: [
                          { value: 'visitor', label: 'Visitor log' },
                          { value: 'activity', label: 'Building activities' },
                        ],
                      },
                    ]}
                    note={
                      <NoteBox>
                        Entry shape only: when, where, what type, who recorded it. The
                        free text, the visitor&apos;s name and the apartment number are
                        deliberately not returned by the API.
                      </NoteBox>
                    }
                  />
                }
              />
              <Route
                path="packages"
                element={
                  <ListScreen
                    resource="packages"
                    columns={PACKAGE_COLUMNS}
                    searchable
                    searchPlaceholder="Carrier, shelf, unit, building, organization…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        // THE STORED VALUES, EXACTLY. `awaiting` was not one of
                        // them — the column holds `awaiting_pickup` — so the
                        // filter built a valid query that could never match a
                        // row, and the screen said "Nothing here." over 636
                        // parcels. `labelFor` supplies the words; the values
                        // stay the API's.
                        options: ['awaiting_pickup', 'picked_up', 'returned'].map(v => ({
                          value: v,
                          label: labelFor(v),
                        })),
                      },
                    ]}
                    note={
                      <NoteBox>
                        Recipient names and tracking numbers are encrypted and are not
                        returned.
                      </NoteBox>
                    }
                  />
                }
              />
              <Route
                path="visitor-passes"
                element={
                  <ListScreen
                    resource="visitor-passes"
                    columns={VISITOR_PASS_COLUMNS}
                    searchable
                    searchPlaceholder="Kind, state, unit, building, organization…"
                    filters={[
                      {
                        name: 'state',
                        label: 'State',
                        // The API's derived states, exactly; labelFor gives the words.
                        options: [
                          'active',
                          'paused',
                          'scheduled',
                          'ended',
                          'off',
                          'pending',
                        ].map(v => ({
                          value: v,
                          label: labelFor(v),
                        })),
                      },
                    ]}
                    note={
                      <NoteBox>
                        Pass shape only: the kind of visitor, the schedule, the unit,
                        whether a resident or the building added it, its state and how
                        often it was used. Visitor names, the note to the desk and the
                        resident are not returned.
                      </NoteBox>
                    }
                  />
                }
              />
              {/* Lobbero Stays plan S3.11: short stays, shape only. */}
              {staysOn ? <Route path="stays-reservations" element={<StaysReservationsScreen />} /> : null}
              {/* Independent Stays IS3: accounts the public Stays sign-up made. */}
              {staysOn ? <Route path="stays-signups" element={<StaysSignupsScreen />} /> : null}
              {/* Independent Stays IS4: Stays billing accounts (read-only). */}
              {staysOn ? <Route path="stays-billing" element={<StaysBillingScreen />} /> : null}
              {/* Independent Stays IS8.4: independent accounts and the closure queue. */}
              {staysOn ? <Route path="stays-accounts" element={<StaysAccountsScreen />} /> : null}
              {staysOn ? <Route path="stays-closures" element={<StaysClosuresScreen {...host} />} /> : null}
              <Route
                path="shifts"
                element={
                  <ListScreen
                    resource="shifts"
                    columns={SHIFT_COLUMNS}
                    searchable
                    searchPlaceholder="Person, role, building, organization…"
                    filters={[
                      {
                        name: 'open',
                        label: 'State',
                        options: [
                          { value: 'true', label: 'Open' },
                          { value: 'false', label: 'Ended' },
                        ],
                      },
                    ]}
                  />
                }
              />
              <Route
                path="access"
                element={
                  <ListScreen
                    resource="access"
                    columns={ACCESS_COLUMNS}
                    searchable
                    searchPlaceholder="Person, scope, reason, building, portfolio…"
                    filters={[
                      {
                        name: 'scope',
                        label: 'Scope',
                        options: ['organization', 'portfolio', 'building', 'unit'].map(
                          v => ({
                            value: v,
                            label: v,
                          })
                        ),
                      },
                    ]}
                  />
                }
              />
              <Route
                path="sessions"
                element={
                  <ListScreen
                    resource="sessions"
                    columns={SESSION_COLUMNS}
                    searchable
                    searchPlaceholder="Person, organization, IP address, browser…"
                    filters={[
                      {
                        name: 'live',
                        label: 'State',
                        options: [
                          { value: 'true', label: 'Live' },
                          { value: 'false', label: 'Ended' },
                        ],
                      },
                    ]}
                  />
                }
              />
              <Route path="notifications" element={<NotificationsScreen />} />
              <Route path="iq" element={<IqScreen />} />
              <Route
                path="iq/requests"
                element={
                  <ListScreen
                    resource="iq/requests"
                    columns={AI_COLUMNS}
                    searchable
                    searchPlaceholder="Feature, model, status, error code, organization…"
                    filters={[
                      {
                        name: 'status',
                        label: 'Status',
                        options: ['ok', 'error', 'blocked', 'timeout'].map(v => ({
                          value: v,
                          label: v,
                        })),
                      },
                    ]}
                  />
                }
              />

              <Route path="server" element={<ServerScreen />} />
              <Route path="system-logs" element={<SystemLogsScreen />} />
              <Route path="backups" element={<BackupsScreen />} />
              <Route path="performance" element={<PerformanceScreen />} />
              <Route path="database" element={<DatabaseScreen />} />
              <Route path="health" element={<HealthScreen />} />
              <Route path="health/:key" element={<IntegrationScreen />} />
              <Route path="integrity" element={<IntegrityScreen />} />
              <Route path="storage" element={<StorageScreen />} />
              <Route path="credentials" element={<CredentialsScreen />} />
              <Route
                path="audit"
                element={
                  <ListScreen
                    resource="audit"
                    columns={AUDIT_COLUMNS}
                    searchable
                    searchPlaceholder="Actor, action, target, organization, reason…"
                    note={
                      <NoteBox>
                        Append-only. This table cannot be updated or deleted by anything —
                        including the database owner — and the application role holds no
                        privilege on it at all.
                      </NoteBox>
                    }
                  />
                }
              />

              <Route
                path="*"
                element={
                  <ErrorBox>
                    No such section. Press <Kbd>g</Kbd> then a letter, or use the rail.
                  </ErrorBox>
                }
              />
            </Routes>
          </Content>
        </Main>

        {askPassword ? (
          <ElevateDialog
            busy={elevation.busy}
            onElevate={elevation.elevate}
            onClose={elevated => {
              setAskPassword(false);
              // If the password prompt was CANCELLED, drop the held intent —
              // otherwise the confirmation would open unauthenticated and fail at
              // the server, which reads as a broken button.
              //
              // The dialog reports its own outcome; this used to read
              // `elevation.elevated` from state that had not re-rendered yet, so
              // a SUCCESSFUL step-up dropped the intent too and the confirmation
              // never opened at all.
              if (!elevated) setPending(null);
            }}
          />
        ) : null}

        {pending && !askPassword ? (
          <ConfirmDialog spec={pending} onClose={() => setPending(null)} />
        ) : null}
      </AdminRoot>
    </ClockSurfaceProvider>
  );
};

export default AdminConsole;
